import { Maximize2, Minimize2 } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { ideLayoutStore, useIdeLayout, type IdeFocus } from "./ideLayoutStore";
import {
  FOCUS_LABEL,
  paneShortcutKeys,
  paneShortcutLabel
} from "./paneShortcuts";

type Props = {
  focus: IdeFocus;
  className?: string;
};

/**
 * A pane header's Maximize control: makes the pane big, then reads Restore
 * Layout until the layout from before is back. The tooltip names the chord.
 */
export const PaneMaximizeButton: FC<Props> = ({ focus, className }) => {
  const on = useIdeLayout().focus === focus;
  const label = on ? "Restore Layout" : FOCUS_LABEL[focus];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          className={cn("pane-expand text-fg-3 hover:text-fg", className)}
          aria-label={label}
          aria-pressed={on}
          aria-keyshortcuts={paneShortcutKeys(focus)}
          data-testid={`maximize-${focus}`}
          onClick={() => ideLayoutStore.toggleFocus(focus)}
        >
          {on ? (
            <Minimize2 className="size-3.5" />
          ) : (
            <Maximize2 className="size-3.5" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="flex items-center gap-2">
        {label}
        <Kbd>{on ? "Esc" : paneShortcutLabel(focus)}</Kbd>
      </TooltipContent>
    </Tooltip>
  );
};
